import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";

import Seo from "../../components/Seo";
import { seoFromPublicPage } from "../../seo/publicPages";
import "./LandingPage.css";
import LandingAboutSection from "./LandingAboutSection";
import LandingFaqSection from "./LandingFaqSection";
import LandingFinalCta from "./LandingFinalCta";
import LandingFooter from "./LandingFooter";
import LandingGuidesSection from "./LandingGuidesSection";
import LandingHeroSection from "./LandingHeroSection";
import LandingNavbar from "./LandingNavbar";
import LandingNewsSection from "./LandingNewsSection";
import LandingPricingSection from "./LandingPricingSection";
import LandingProblemSection from "./LandingProblemSection";
import LandingSolutionSection from "./LandingSolutionSection";

const LANDING_CHROME_COLOR = "#2a2544";

function LandingPage() {
  const { pathname, hash } = useLocation();
  const isFirstLoad = useRef(true);

  useEffect(() => {
    const themeMeta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    const previousTheme = themeMeta?.getAttribute("content");
    const previousScheme = document.documentElement.style.colorScheme;
    themeMeta?.setAttribute("content", LANDING_CHROME_COLOR);
    document.documentElement.style.colorScheme = "dark";
    return () => {
      if (previousTheme) themeMeta?.setAttribute("content", previousTheme);
      document.documentElement.style.colorScheme = previousScheme;
    };
  }, []);

  useEffect(() => {
    if (pathname !== "/") return;

    const id = hash.replace(/^#/, "");

    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      if (id) {
        const el = document.getElementById(id);
        if (!el) return;
        const t = window.setTimeout(() => {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 0);
        return () => window.clearTimeout(t);
      }

      let started = false;
      const trySmoothTop = () => {
        if (started || window.scrollY < 8) return;
        started = true;
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
      };

      const interval = window.setInterval(trySmoothTop, 16);
      const fallback = window.setTimeout(() => {
        window.clearInterval(interval);
        trySmoothTop();
      }, 700);
      const onLoad = () => {
        window.setTimeout(trySmoothTop, 80);
      };
      if (document.readyState === "complete") onLoad();
      else window.addEventListener("load", onLoad, { once: true });
      const onPageShow = (event: PageTransitionEvent) => {
        if (event.persisted) window.setTimeout(trySmoothTop, 50);
      };
      window.addEventListener("pageshow", onPageShow);

      return () => {
        window.clearInterval(interval);
        window.clearTimeout(fallback);
        window.removeEventListener("pageshow", onPageShow);
      };
    }

    if (!id) return;
    const el = document.getElementById(id);
    if (!el) return;
    const t = window.setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 0);
    return () => window.clearTimeout(t);
  }, [pathname, hash]);

  return (
    <div className="landing-page">
      <Seo {...seoFromPublicPage("/")} />

      <div className="landing-page__glow landing-page__glow--left" aria-hidden="true" />
      <div className="landing-page__glow landing-page__glow--right" aria-hidden="true" />

      <LandingNavbar />

      <main>
        <LandingHeroSection />
        <LandingProblemSection />
        <LandingSolutionSection />
        <LandingPricingSection />
        <LandingGuidesSection />
        <LandingNewsSection />
        <LandingFaqSection />
        <LandingAboutSection />
        <LandingFinalCta />
      </main>

      <LandingFooter />
    </div>
  );
}

export default LandingPage;
